// IMPORT/LIBRERIE
import React, { useState, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";

// CONFIGURAZIONI/COSTANTI
import { RECAPTCHA_SITE_KEY, AGENCY_ID, IUBENDA_SITE_ID, DEFAULT_AGENCY_NAME, appConfig } from "./config/appConfig";
import i18n from "./config/i18n/i18n";

// COMPONENTI
import ErrorBoundary from "./components/Error/ErrorBoundary";
import NotFound from "./components/Error/NotFound";
import HomePage from "./components/Home/Home";
import AboutUs from "./components/AboutUs/AboutUs";
import Contacts from "./components/Contacts/Contacts";

import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Immobili from "./components/Properties/Immobili";
import ImmobiliSuccess from "./components/Properties/ImmobiliSuccess";
import PropertySold from "./components/Property/PropertySold";
import Sell from "./components/Static/Vendi";
import Blog from "./components/Blog/Blog";
import BlogDetail from "./components/Blog/BlogDetail";
import Luxury from "./components/Luxury/Luxury";
//import SellHome from "./components/VendereCasa/VendereCasa";
import Buy from "./components/Static/Cerca";
import Dati from "./components/Policies/Routes/Dati";
import PropertyDetail from "./components/Property/Property";
import IubendaButton from "./components/Iubenda/IubendaButton";
import Loader from "./components/Loaders/Loader";
import LanguageSelector from "./components/Language/LanguageSelector";
import LanguageRouteHandler from "./components/Language/LanguageRouteHandler";
import RecaptchaWrapper from "./components/Wrappers/RecaptchaWrapper";
import PolicyPageRoute from './components/Policies/PolicyPageRoute';
import { getPolicyIdForLang } from "./utils/getPolicyIdForLang";
import WhatsAppButton from "./components/Widgets/WhatsappWidget";
import HolidayPopup from "./components/Popups/HolidayPopup";

// HOOK
import { useGetAgencyDetails, Agency } from "./hooks/useGetAgencyDetails";

// FOGLI DI STILE
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '@flaticon/flaticon-uicons/css/all/all.css';
import "./App.css";
import ScrollToTop from "./utils/scrollToTop";
import TrailingSlashNormalizer from "./utils/trailingSlashNormalizer";
import WorkingMethod from "./components/AboutUs/WorkingMethod";


interface AppProps {
  agencyData: Agency | null;
}

interface AppShellProps extends AppProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ agencyData, children }) => {
  const { i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (lang: string) => {
    i18nInstance.changeLanguage(lang).then(() => {
      if (appConfig.integrations.multilang) {
        const currentPath = location.pathname;
        const newPath = currentPath.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lang}`);
        if (!/^\/[a-z]{2}/.test(currentPath)) {
          navigate(`/${lang}${currentPath}`);
        } else {
          navigate(newPath, { replace: true });
        }
      }
    });
  };

  const headerAgencyData = {
    agency_name: agencyData?.name ?? appConfig.agency.defaults.name,
    phone1: agencyData?.phone1 ?? appConfig.agency.defaults.phone1,
    mobile: agencyData?.mobile ?? appConfig.agency.defaults.mobile,
    mobile_visible: true,
    international_prefix: appConfig.agency.defaults.intlPrefix,
    whatsapp: true,
    facebook: true,
    instagram: true,
    youtube: true,
    twitter: false,
    linkedin: false,
    pinterest: false,
    cookie_solution_iubenda: true,
  };

  if (!RECAPTCHA_SITE_KEY) {
    console.error("App.tsx: RECAPTCHA_SITE_KEY non è definito!");
    return <div>Errore di configurazione: reCAPTCHA non disponibile.</div>;
  }

  const currentAppLanguage = i18nInstance.language.split('-')[0];
  const buttonPolicyId = getPolicyIdForLang(currentAppLanguage);

  return (
    <div className="App">
      <Header
        agencyData={headerAgencyData}
        languageSelector={
          appConfig.ui.header.showLanguageSelector ? <LanguageSelector handleLanguageChange={handleLanguageChange} /> : <></>
        }
      />

      {children}

      <IubendaButton siteId={IUBENDA_SITE_ID} cookiePolicyId={buttonPolicyId} showBanner={appConfig.integrations.iubenda.showBanner} />

      <Footer />

      {appConfig.ui.showWhatsAppButton && <WhatsAppButton />}
      {appConfig.features.holidayPopup.enabled && <HolidayPopup />}

      <div itemScope itemType="http://schema.org/RealEstateAgent">
        <meta itemProp="legalname" content={appConfig.agency.nameLegal} />
        <div itemProp="address" itemScope itemType="http://schema.org/PostalAddress">
          <meta itemProp="streetAddress" content={agencyData?.address_details.address} />
          <meta itemProp="addressLocality" content={agencyData?.address_details.city_name} />
          <meta itemProp="addressRegion" content={agencyData?.address_details.sigla} />
          <meta itemProp="postalCode" content={String(agencyData?.address_details.city_cap || "")} />
        </div>
        {appConfig.map.centerMapData?.center_lat && appConfig.map.centerMapData?.center_lng && (
          <div itemProp="geo" itemScope itemType="http://schema.org/GeoCoordinates">
            <meta itemProp="latitude" content={String(appConfig.map.centerMapData.center_lat)} />
            <meta itemProp="longitude" content={String(appConfig.map.centerMapData.center_lng)} />
          </div>
        )}
        <meta itemProp="url" content={agencyData?.website} />
        <meta itemProp="taxID" content={agencyData?.vat} />
        <meta itemProp="telephone" content={agencyData?.phone1} />
      </div>

    </div>
  );
};

const App: React.FC<AppProps> = ({ agencyData }) => {
  const [showAgents] = useState(true);
  const showBlogRoutes = appConfig.ui.routes.showBlogRoutes;
  const showSuccessRoutes = appConfig.ui.routes.showSuccessRoutes;
  const langPrefix = appConfig.integrations.multilang ? "/:lng" : "";

  return (
    <AppShell agencyData={agencyData}>
      <Routes>

        <Route path={`${langPrefix}/`} element={
          <RecaptchaWrapper>
            <HomePage
              seo={true}
              vetrina2={appConfig.ui.homePage.vetrina2}
              pageType={appConfig.ui.homePage.pageType}
              filters={appConfig.ui.homePage.filters}
              showServices={appConfig.ui.homePage.showServices}
              showLocationSearch={appConfig.ui.homePage.showLocationSearch}
              showFooterBannerVendi={appConfig.ui.homePage.showFooterBannerVendi}
              showPresentationSection={appConfig.ui.homePage.showPresentationSection}
              showSocialSection={appConfig.ui.homePage.showSocialSection}
              showVetrina1={appConfig.ui.homePage.showVetrina1}
            />
          </RecaptchaWrapper>
        }
        />

        <Route path={`${langPrefix}/immobili/*`} element={<Immobili />} />
        <Route path={`${langPrefix}/immobili`} element={<Immobili />} />

        <Route path={`${langPrefix}/immobile/:idSlug`} element={
          <RecaptchaWrapper>
            <PropertyDetail />
          </RecaptchaWrapper>
        }
        />

        <Route path={`${langPrefix}/chi-siamo`} element={<AboutUs agencyId={AGENCY_ID} />} />
        <Route path={`${langPrefix}/metodo-di-lavoro`} element={<WorkingMethod agencyId={AGENCY_ID} />} />
        <Route path={`${langPrefix}/luxury`} element={<Luxury />} />

        <Route path={`${langPrefix}/contatti`} element={
          <RecaptchaWrapper>
            <Contacts
              contactForm={true}
              agencyId={AGENCY_ID}
              showAgents={showAgents}
            />
          </RecaptchaWrapper>
        }
        />

        <Route path={`${langPrefix}/dati`} element={<Dati agencyId={AGENCY_ID} />} />
        <Route path={`${langPrefix}/privacy`} element={<PolicyPageRoute policyType="privacy" />} />
        <Route path={`${langPrefix}/cookies`} element={<PolicyPageRoute policyType="cookies" />} />

        <Route path={`${langPrefix}/cerca`} element={
          <RecaptchaWrapper>
            <Buy />
          </RecaptchaWrapper>
        }
        />
        <Route path={`${langPrefix}/vendi-casa`} element={
          <RecaptchaWrapper>
            <Sell agencyId={AGENCY_ID} />
          </RecaptchaWrapper>
        }
        />

        {showBlogRoutes && (
          <>
            <Route path={`${langPrefix}/blog`} element={<Blog agencyId={AGENCY_ID} />} />
            <Route path={`${langPrefix}/articolo/:articleSlug`} element={<BlogDetail agencyId={AGENCY_ID} />} />
          </>
        )}

        {showSuccessRoutes && (
          <>
            <Route path={`${langPrefix}/immobili-successi`} element={<ImmobiliSuccess agencyId={AGENCY_ID} />} />
            <Route path={`${langPrefix}/immobili-successi/:idSlug`} element={<PropertySold />} />
          </>
        )}

        <Route path="*" element={<NotFound message="" />} />

      </Routes>
    </AppShell>
  );
};

// --- Wrapper per I18n e Caricamento Iniziale ---
function AppWithI18n() {
  // RIMOSSA LA LOGICA DI CARICAMENTO PER I18N
  // const [i18nIsInitialized, setI18nIsInitialized] = useState(false);

  const { agency, loading: isLoadingAgency, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

  // RIMOSSO useEffect per i18n.init() o per l'evento 'initialized'

  // Ora isLoading dipende solo dal caricamento dei dati dell'agenzia
  const isLoading = isLoadingAgency;

  const loaderAgencyData = {
    agency_name: agency?.name ?? DEFAULT_AGENCY_NAME
  };

  useEffect(() => {
    if (agencyError) {
      console.error("Errore caricamento dati agenzia:", agencyError);
    }
  }, [agencyError]);

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {isLoading ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <ErrorBoundary>
          <LanguageRouteHandler>
            <App agencyData={agency} />
          </LanguageRouteHandler>
        </ErrorBoundary>
      )}
    </I18nextProvider>
  );
}

function BlogPageWithI18n() {
  const { agency, loading: isLoadingAgency, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

  const isLoading = isLoadingAgency;
  const loaderAgencyData = {
    agency_name: agency?.name ?? DEFAULT_AGENCY_NAME
  };

  useEffect(() => {
    if (agencyError) {
      console.error("Errore caricamento dati agenzia:", agencyError);
    }
  }, [agencyError]);

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {isLoading ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <ErrorBoundary>
          <LanguageRouteHandler>
            <AppShell agencyData={agency}>
              <Blog agencyId={AGENCY_ID} />
            </AppShell>
          </LanguageRouteHandler>
        </ErrorBoundary>
      )}
    </I18nextProvider>
  );
}

function BlogDetailWithI18n() {
  const { agency, loading: isLoadingAgency, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

  const isLoading = isLoadingAgency;
  const loaderAgencyData = {
    agency_name: agency?.name ?? DEFAULT_AGENCY_NAME
  };

  useEffect(() => {
    if (agencyError) {
      console.error("Errore caricamento dati agenzia:", agencyError);
    }
  }, [agencyError]);

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {isLoading ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <ErrorBoundary>
          <LanguageRouteHandler>
            <AppShell agencyData={agency}>
              <BlogDetail agencyId={AGENCY_ID} />
            </AppShell>
          </LanguageRouteHandler>
        </ErrorBoundary>
      )}
    </I18nextProvider>
  );
}

const getSsgLanguages = (): string[] => {
  if (!appConfig.integrations.multilang) {
    return ["it"];
  }

  const supported = i18n.options.supportedLngs;
  if (Array.isArray(supported) && supported.length > 0) {
    const filtered = supported.filter((lng) => lng !== "cimode");
    if (filtered.length > 0) {
      return filtered;
    }
  }

  return ["it", "en"];
};

const fetchBlogArticleSlugs = async (): Promise<string[]> => {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
  if (!apiBaseUrl) {
    console.warn("SSG: VITE_APP_API_BASE_URL non definito, salto pre-render articoli blog.");
    return [];
  }

  try {
    const response = await fetch(`${apiBaseUrl}/blog/articoli`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        agencyId: AGENCY_ID,
        limit: 1000,
        withCount: false,
      }),
    });

    if (!response.ok) {
      console.warn(`SSG: fetch articoli fallita (${response.status}), salto pre-render articoli blog.`);
      return [];
    }

    const payload = await response.json();
    const articles = Array.isArray(payload?.data) ? payload.data : [];

    return articles
      .map((article: { id?: number; seoString?: string | null }) => {
        if (typeof article?.id !== "number") {
          return null;
        }
        return `${article.id}-${article.seoString ?? "articolo"}`;
      })
      .filter((slug: string | null): slug is string => Boolean(slug));
  } catch (error) {
    console.warn("SSG: errore durante il fetch articoli blog, salto pre-render articoli.", error);
    return [];
  }
};

interface PropertyListItem {
  id?: number;
}

interface PropertyDetailResponse {
  propertyId?: number;
  typologyDetails?: { seoString?: string };
  contractDetails?: { contractName?: string };
  indirizzo?: {
    comuneSeoString?: string;
    comuneIt?: string;
    frazioneSeoString?: string;
    quartiereSeoString?: string;
  };
}

const slugify = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const fetchPropertySlugs = async (): Promise<string[]> => {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
  if (!apiBaseUrl) {
    console.warn("SSG: VITE_APP_API_BASE_URL non definito, salto pre-render immobili.");
    return [];
  }

  try {
    const listResponse = await fetch(`${apiBaseUrl}/properties`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        agencyId: AGENCY_ID,
        limit: 10000,
        offset: 0,
        withCount: false,
        status: [2, 3, 9, 11],
        filters: {},
        portal: 56,
      }),
    });

    if (!listResponse.ok) {
      console.warn(`SSG: fetch immobili fallita (${listResponse.status}), salto pre-render immobili.`);
      return [];
    }

    const listPayload = await listResponse.json();
    const properties = Array.isArray(listPayload?.data) ? listPayload.data : [];
    const ids = properties
      .map((property: PropertyListItem) => property?.id)
      .filter((id: number | undefined): id is number => typeof id === "number");

    const slugs: string[] = [];

    for (const propertyId of ids) {
      try {
        const detailResponse = await fetch(`${apiBaseUrl}/property`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            propertyId,
            limit: 1,
            offset: 0,
          }),
        });

        if (!detailResponse.ok) {
          continue;
        }

        const property = (await detailResponse.json()) as PropertyDetailResponse;
        if (!property?.propertyId) {
          continue;
        }

        const propertyTypeSeo = property.typologyDetails?.seoString || "immobile";
        const contractSeo = (property.contractDetails?.contractName || "dettaglio").toLowerCase().replace(/\s+/g, "-");
        const citySeo = property.indirizzo?.comuneSeoString || slugify(property.indirizzo?.comuneIt || "");
        const locationSeo = property.indirizzo?.frazioneSeoString || property.indirizzo?.quartiereSeoString || "";
        const fullSlug = `${property.propertyId}-${propertyTypeSeo}-${contractSeo}-${citySeo}${locationSeo ? `-${locationSeo}` : ""}`;

        slugs.push(fullSlug);
      } catch {
        // skip invalid property details and continue
      }
    }

    return Array.from(new Set(slugs));
  } catch (error) {
    console.warn("SSG: errore durante il fetch immobili, salto pre-render immobili.", error);
    return [];
  }
};

function PropertyDetailWithI18n() {
  const { agency, loading: isLoadingAgency, error: agencyError } = useGetAgencyDetails(AGENCY_ID);

  const isLoading = isLoadingAgency;
  const loaderAgencyData = {
    agency_name: agency?.name ?? DEFAULT_AGENCY_NAME
  };

  useEffect(() => {
    if (agencyError) {
      console.error("Errore caricamento dati agenzia:", agencyError);
    }
  }, [agencyError]);

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {isLoading ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <ErrorBoundary>
          <LanguageRouteHandler>
            <AppShell agencyData={agency}>
              <RecaptchaWrapper>
                <PropertyDetail />
              </RecaptchaWrapper>
            </AppShell>
          </LanguageRouteHandler>
        </ErrorBoundary>
      )}
    </I18nextProvider>
  );
}

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: AppWithI18n,
  },
  {
    path: "/blog",
    Component: BlogPageWithI18n,
  },
  {
    path: "/articolo/:articleSlug",
    Component: BlogDetailWithI18n,
    getStaticPaths: async () => {
      const slugs = await fetchBlogArticleSlugs();
      return slugs.map((articleSlug) => `articolo/${articleSlug}`);
    },
  },
  {
    path: "/blog",
    Component: BlogPageWithI18n,
    getStaticPaths: () => {
      const languages = getSsgLanguages();
      return languages.map((lng) => `${lng}/blog`);
    },
  },
  {
    path: "/articolo/:articleSlug",
    Component: BlogDetailWithI18n,
    getStaticPaths: async () => {
      const languages = getSsgLanguages();
      const slugs = await fetchBlogArticleSlugs();
      return languages.flatMap((lng) => slugs.map((articleSlug) => `${lng}/articolo/${articleSlug}`));
    },
  },
  {
    path: "/immobile/:idSlug",
    Component: PropertyDetailWithI18n,
    getStaticPaths: async () => {
      const slugs = await fetchPropertySlugs();
      return slugs.map((idSlug) => `immobile/${idSlug}`);
    },
  },
  {
    path: "/immobile/:idSlug",
    Component: PropertyDetailWithI18n,
    getStaticPaths: async () => {
      const languages = getSsgLanguages();
      const slugs = await fetchPropertySlugs();
      return languages.flatMap((lng) => slugs.map((idSlug) => `${lng}/immobile/${idSlug}`));
    },
  },
  {
    path: "*",
    Component: AppWithI18n,
  },
]

export default AppWithI18n;
